<template>
  <div v-if="automation">
    <div class="row">
      <div class="col-md-6">
        <div class="card">
          <div class="card-body text-form">
            <div class="row no-gutters align-items-center">
              <div class="col-auto">{{ 'when_a' | t }}</div>
              <div class="col-auto flex-grow-1 px-2">
                <input class="form-control form-control-sm" v-model="automation.trigger.model_type" />
              </div>
            </div>
            <div class="row no-gutters align-items-center">
              <div class="col-auto">{{ 'is' | t }}</div>
              <div class="col-auto flex-grow-1 px-2">
                <select class="form-control form-control-sm" v-model="automation.trigger.event">
                  <option></option>
                  <option value="created">{{ 'created' | t }}</option>
                  <option value="updated">{{ 'updated' | t }}</option>
                  <option value="about_to_start">{{ 'about_to_start' | t }}</option>
                </select>
              </div>
            </div>
            <div
              class="row no-gutters align-items-center"
              :class="{ 'text-muted': automation.trigger.expression == '' }"
            >
              <div class="col-auto">{{ 'and' | t }}</div>
              <div class="col-auto flex-grow-1 px-2">
                <input class="form-control form-control-sm" placeholder="" v-model="automation.trigger.expression" />
              </div>
            </div>
            <div class="row">
              <div class="col">
                <i class="help-text fa fa-support mr-2"></i
                ><a
                  class="help-text"
                  href="https://support.webshipper.com/automations/how-to-use-automations"
                  target="_blank"
                  >{{ 'view_instructions' | t }}</a
                >
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="card">
          <div class="card-body">
            <div class="form-group">
              <div class="row">
                <div class="col">
                  <div class="my-2">{{ 'then' | t | titlecase }}</div>
                  <div class="filter-wrapper mb-2" v-for="(action, idx) in automation.actions">
                    <div class="row mb-2">
                      <div class="col-md-10 text-left pl-4">
                        <strong>{{ action.job_type | t }}</strong>
                      </div>
                      <div class="col text-right">
                        <i class="fa fa-times text-light" role="button" @click="remove_action(idx)"></i>
                      </div>
                    </div>
                    <div class="row mb-2" v-for="(attr, v) in action.additional_attributes">
                      <div class="col-md-4 text-left pl-4">
                        {{ attr_settings(action.job_type, attr.attr_key).i18n | t }}
                      </div>
                      <div class="col-md-8 pr-4">
                        <shipping-rates-selector
                          v-model="attr.attr_value"
                          v-if="attr_settings(action.job_type, attr.attr_key).type === 'shipping_rate'"
                        ></shipping-rates-selector>
                        <mail-templates-selector
                          v-model="attr.attr_value"
                          v-else-if="attr_settings(action.job_type, attr.attr_key).type === 'mail_template'"
                        ></mail-templates-selector>
                        <select
                          class="form-control form-control-sm"
                          v-model="attr.attr_value"
                          v-else-if="attr_settings(action.job_type, attr.attr_key).type == 'enum'"
                        >
                          <option v-for="v in attr_settings(action.job_type, attr.attr_key).enums" :value="v">
                            {{ v }}
                          </option>
                        </select>
                        <textarea
                          class="form-control form-control-sm"
                          v-model="attr.attr_value"
                          v-else-if="attr_settings(action.job_type, attr.attr_key).type === 'text'"
                        ></textarea>
                        <input class="form-control form-control-sm" v-model="attr.attr_value" v-else />
                      </div>
                    </div>
                    <div class="row mb-1" v-if="get_job_type(action.job_type).support_link">
                      <div class="col text-center">
                        <i class="help-text fa fa-support mr-2"></i
                        ><a class="help-text" :href="get_job_type(action.job_type).support_link.url" target="_blank">{{
                          get_job_type(action.job_type).support_link.name
                        }}</a>
                      </div>
                    </div>
                  </div>
                  <div>
                    <select class="form-control form-control-sm" @change="add_action(new_action)" v-model="new_action">
                      <option :value="null" disabled="disabled">{{ 'add_action' | t }}</option>
                      <option :value="job_type" v-for="job_type in suitable_job_types">{{ job_type.key | t }}</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- em.text-muted With Automations you can build custom workflows on the Webshipper platform using highly flexible Triggers and Actions-->
    </div>
  </div>
  <!---->
</template>
<script>
import _ from 'lodash';
import Api from '@/store/jsonapi';
import Alerts from '@/utils/alerts';

export default {
  props: ['value'],
  data: function () {
    return {
      job_types: [
        {
          key: 'CreateShipmentJob',
          additional_attributes: [],
          supported_triggers: ['Shipment', 'Order']
        },
        {
          key: 'UpdateRecordJob',
          additional_attributes: [
            { key: 'attribute', type: 'string', i18n: 'attribute' },
            { key: 'value', type: 'string', i18n: 'value' }
          ],
          supported_triggers: ['*'],
          support_link: {
            name: this.$t('view_instructions'),
            url: 'https://support.webshipper.com/automations/update-attribute/update-an-attribute-with-automations'
          }
        },
        {
          key: 'BundleOrderJob',
          additional_attributes: [],
          supported_triggers: ['Order'],
          support_link: {
            name: this.$t('view_instructions'),
            url: 'https://support.webshipper.com/automations/merge-bundles-in-to-one-orderline/merge-bundles-in-to-one-order-line-with-automations'
          }
        },
        {
          key: 'CreateReturnLabelJob',
          additional_attributes: [
            {
              key: 'shipping_rate_id',
              type: 'shipping_rate',
              i18n: 'shipping_rate'
            }
          ],
          supported_triggers: ['Order', 'Shipment']
        },
        {
          key: 'SlackMessageJob',
          additional_attributes: [
            { key: 'url', type: 'string', i18n: 'URL For Incoming Webhook' },
            {
              key: 'template',
              type: 'text',
              i18n: 'Liquid Template for Message'
            }
          ],
          supported_triggers: ['*']
        },
        {
          key: 'UpdateOrderLineJob',
          additional_attributes: [
            {
              key: 'attribute',
              type: 'enum',
              i18n: 'attribute',
              enums: [
                'sku',
                'description',
                'quantity',
                'location',
                'tarif_number',
                'country_of_origin',
                'unit_price',
                'vat_percent',
                'status',
                'ext_ref',
                'weight',
                'weight_unit'
              ]
            },
            { key: 'value', type: 'string', i18n: 'value' },
            {
              key: 'order_line_expression',
              type: 'string',
              i18n: 'order_line_expression'
            }
          ],
          supported_triggers: ['Order'],
          support_link: {
            name: this.$t('view_instructions'),
            url: 'https://support.webshipper.com/automations/update-order-line/update-order-lines-with-automations'
          }
        },
        {
          key: 'MarkInvoiceSentJob',
          supported_triggers: ['Global::Invoice']
        },
        {
          key: 'SendEmailJob',
          additional_attributes: [
            {
              key: 'mail_to',
              i18n: 'Mail to',
              type: 'string'
            },
            {
              key: 'mail_template_id',
              type: 'mail_template',
              i18n: 'Mail template'
            }
          ],
          supported_triggers: ['*']
        }
      ],
      new_action: null
    };
  },
  computed: {
    automation() {
      return this.value;
    },
    suitable_job_types() {
      let model_type = this.automation.trigger.model_type;
      return _.filter(this.job_types, (job_type) => {
        return job_type.supported_triggers.indexOf(model_type) > -1 || job_type.supported_triggers.indexOf('*') > -1;
      });
    }
  },
  methods: {
    attr_settings(key, attr) {
      let job_type = _.find(this.job_types, (t) => {
        return key == t.key;
      });
      return _.find(job_type.additional_attributes, (t) => {
        return t.key == attr;
      });
    },
    get_job_type(key) {
      return _.find(this.job_types, (t) => {
        return key == t.key;
      });
    },
    save: async function () {
      await Api.create('automations', this.automation);
      this.$router.push({ name: 'automations' });
    },
    add_action(job_type) {
      if (this.new_action) {
        this.automation.actions.push({
          job_type: job_type.key,
          async: false,
          additional_attributes: _.map(job_type.additional_attributes, (attr) => ({
            attr_key: attr.key,
            attr_value: ''
          }))
        });
        this.new_action = null;
      }
    },
    remove_action(idx) {
      console.log(idx);
      this.automation.actions.splice(idx, 1);
    }
  }
};
</script>

<style scoped>
.flex-grow-1 {
  flex-grow: 1;
}

.flex-grow-2 {
  flex-grow: 2;
}

.form-inline {
  line-height: 150%;
}

.text-form .form-control {
  margin-top: .5rem;
  margin-bottom: .5rem;
}

.filter-wrapper {
  display: block;
  margin-top: 0 !important;
  margin-left: 0 !important;
}
</style>
